export interface DashboardPayment {
  id: string; student_id: string; category_name: string; amount_paid: number; total_amount: number;
  created_at: string; payment_date: string | null; end_date: string | null; status: string;
}
export interface DashboardStudent { id: string; first_name: string; last_name: string; is_active?: boolean; }
export interface DashboardExpense { amount: number; currency: string | null; expense_date: string; }
export interface DashboardCategory { name: string; currency: string | null; }
export interface DashboardData { payments: DashboardPayment[]; students: DashboardStudent[]; expenses: DashboardExpense[]; categories: DashboardCategory[]; }

export function summarizeFinances(data: DashboardData, year: number, currency: string) {
  const categoryCurrency = new Map<string, string | null>();
  for (const category of data.categories) {
    // A reused category name with conflicting currencies cannot identify an old record's currency.
    if (categoryCurrency.has(category.name) && categoryCurrency.get(category.name) !== category.currency) categoryCurrency.set(category.name, null);
    else if (!categoryCurrency.has(category.name)) categoryCurrency.set(category.name, category.currency);
  }
  const months = Array.from({ length: 12 }, (_, month) => ({ month, income: 0, expenses: 0 }));
  let pending = 0;
  for (const payment of data.payments) {
    if (categoryCurrency.get(payment.category_name) !== currency) continue;
    pending += Math.max(0, (payment.total_amount || 0) - (payment.amount_paid || 0));
    // This is the date on the existing payment record, not an invented installment ledger.
    const date = (payment.payment_date || payment.created_at || '').slice(0, 10);
    if (Number(date.slice(0, 4)) !== year) continue;
    const month = Number(date.slice(5, 7)) - 1;
    if (months[month]) months[month].income += (payment.amount_paid || 0) / 100;
  }
  for (const expense of data.expenses) {
    if (expense.currency !== currency || Number(expense.expense_date.slice(0, 4)) !== year) continue;
    const month = Number(expense.expense_date.slice(5, 7)) - 1;
    if (months[month]) months[month].expenses += (expense.amount || 0) / 100;
  }
  const income = months.reduce((sum, m) => sum + m.income, 0);
  const expenses = months.reduce((sum, m) => sum + m.expenses, 0);
  return { months, income, expenses, net: income - expenses, pending: pending / 100,
    unclassified: data.payments.filter(p => !categoryCurrency.get(p.category_name)).length + data.expenses.filter(e => !e.currency).length };
}

export function subscriptionRenewals(data: DashboardData, today: string, days: number) {
  const latest = new Map<string, DashboardPayment>();
  for (const payment of data.payments) {
    if (!payment.end_date || !/^\d{4}-\d{2}-\d{2}/.test(payment.end_date)) continue;
    const previous = latest.get(payment.student_id);
    if (!previous || payment.end_date > previous.end_date!) latest.set(payment.student_id, payment);
  }
  const active = data.students.filter(s => s.is_active !== false);
  const renewals = active.flatMap(student => {
    const payment = latest.get(student.id);
    if (!payment?.end_date) return [];
    const remaining = Math.round((Date.parse(payment.end_date.slice(0,10) + 'T00:00:00Z') - Date.parse(today + 'T00:00:00Z')) / 86400000);
    return Number.isFinite(remaining) && remaining >= -30 && remaining <= days ? [{ student, payment, remaining }] : [];
  }).sort((a,b) => a.remaining - b.remaining);
  return { renewals, missingDates: active.filter(s => !latest.has(s.id)).length, activeStudents: active.filter(s=>s.is_active === true).length, unknownStatuses: data.students.filter(s=>typeof s.is_active !== 'boolean').length };
}
